import React, { useState, useEffect, useRef } from 'react'
import { useFitStore } from '../store/useFitStore'
import { useNavigate } from 'react-router-dom'
import Button from '../components/shared/Button'
import Card from '../components/shared/Card'
import Input from '../components/shared/Input'
import CelebrationOverlay from '../components/shared/CelebrationOverlay'
import VideoEmbed from '../components/shared/VideoEmbed'
import type { Achievement, Exercise, Workout } from '../store/types'

const WorkoutPlayer: React.FC = () => {
  const { sessions, logSet, setSessionStatus, user, checkAchievements, showToast } = useFitStore();
  const navigate = useNavigate();

  // Today's session, flattened to the player's workout shape (sections → one
  // exercise list). SetPrescription collapses to the weight+reps logger; the
  // prescribed reps seed the input, and supersetId carries the round-robin.
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const session = sessions.find((s) => s.clientId === user?.id && s.date === today);
  const workout: Workout | undefined = session
    ? {
        id: session.id,
        title: session.title || 'Session',
        exercises: session.sections.flatMap((sec) =>
          sec.exercises.map((pe) => ({
            id: pe.id,
            name: pe.name,
            videoUrl: pe.videoUrl,
            sets: Math.max(pe.sets.length, 1),
            reps: pe.sets[0]?.reps ?? (pe.sets[0]?.seconds ? `${pe.sets[0].seconds}s` : pe.sets[0]?.laps ? `${pe.sets[0].laps} laps` : ''),
            restSeconds: 60,
            supersetId: pe.supersetId,
          }))
        ),
      }
    : undefined;

  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [currentSet, setCurrentSet] = useState(1);
  const [restTimer, setRestTimer] = useState(0);
  const [weight, setWeight] = useState('');
  const [reps, setReps] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [newAchievements, setNewAchievements] = useState<Achievement[]>([]);
  
  const [isRecording, setIsRecording] = useState(false);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);

  const currentExercise = workout?.exercises[currentExerciseIndex];

  useEffect(() => {
    if (currentExercise) setReps(currentExercise.reps);
  }, [currentExerciseIndex, currentExercise?.id]);

  useEffect(() => {
    let interval: number | any;
    if (restTimer > 0) {
      interval = setInterval(() => setRestTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [restTimer]);

  const handleCompleteSet = async () => {
    if (!currentExercise || !workout || !user) return;
    if (!weight || !reps) {
      showToast('Please enter weight and reps', 'error');
      return;
    }

    setIsSaving(true);
    try {
      // Form-video persistence lands in Phase 5 (Supabase Storage); log without it for now.
      await logSet(workout.id, workout.title, currentExercise.id, weight, reps, '');
      setVideoBlob(null);

      // --- ROUND ROBIN LOGIC ---
      const exercises = workout.exercises;
      const sid = currentExercise.supersetId;

      if (sid) {
        const group = exercises.filter(e => e.supersetId === sid);
        const currentIndexInGroup = group.findIndex(e => e.id === currentExercise.id);
        
        if (currentIndexInGroup < group.length - 1) {
          const nextExId = group[currentIndexInGroup + 1].id;
          const nextExGlobalIndex = exercises.findIndex(e => e.id === nextExId);
          setCurrentExerciseIndex(nextExGlobalIndex);
        } else {
          if (currentSet < currentExercise.sets) {
            const firstExId = group[0].id;
            const firstExGlobalIndex = exercises.findIndex(e => e.id === firstExId);
            setCurrentExerciseIndex(firstExGlobalIndex);
            setCurrentSet(prev => prev + 1);
            setRestTimer(currentExercise.restSeconds || 60);
          } else {
            goToNextBlock(exercises, currentExerciseIndex);
          }
        }
      } else {
        if (currentSet < currentExercise.sets) {
          setCurrentSet(prev => prev + 1);
          setRestTimer(currentExercise.restSeconds || 60);
        } else {
          goToNextBlock(exercises, currentExerciseIndex);
        }
      }
    } catch (err: any) {
      showToast(`Error saving: ${err.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const goToNextBlock = async (exercises: Exercise[], lastIdx: number) => {
    if (lastIdx < exercises.length - 1) {
      setCurrentExerciseIndex(lastIdx + 1);
      setCurrentSet(1);
      setRestTimer(exercises[lastIdx].restSeconds || 60);
    } else {
      if (session) setSessionStatus(session.id, 'completed');
      const unlocked = await checkAchievements();
      if (unlocked.length > 0) {
        setNewAchievements(unlocked);
      } else {
        showToast('Workout Complete! Great job!');
        navigate('/client');
      }
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        setVideoBlob(new Blob(chunks, { type: 'video/webm' }));
        stream.getTracks().forEach(track => track.stop());
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err) { showToast('Camera access denied', 'error'); }
  };

  if (!currentExercise) return <div className="main-content">No active workout.</div>;

  return (
    <div className="main-content" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {newAchievements.length > 0 && <CelebrationOverlay achievement={newAchievements[0]} onClose={() => navigate('/client')} />}

      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-lg)' }}>
        <div>
          <h1 style={{ fontSize: '1.2rem' }}>{workout?.title}</h1>
          <p className="text-muted" style={{ fontSize: '0.8rem' }}>
            {currentExercise.supersetId ? <span className="text-accent" style={{ fontWeight: 800 }}>SUPERSET ROUND {currentSet}</span> : `Exercise ${currentExerciseIndex + 1} of ${workout?.exercises.length}`}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/client')} size="sm">END</Button>
      </header>

      <div style={{ background: 'var(--bg-tertiary)', height: '4px', borderRadius: 'var(--radius-full)', marginBottom: 'var(--spacing-xl)' }}>
        <div style={{ 
          background: 'var(--accent-primary)', height: '100%', borderRadius: 'var(--radius-full)',
          width: `${((currentExerciseIndex) / (workout?.exercises.length || 1)) * 100}%`,
          transition: 'width 0.3s ease'
        }}></div>
      </div>

      <Card style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', marginBottom: 'var(--spacing-md)', overflow: 'hidden' }}>
        {isRecording ? (
          <div style={{ width: '100%', height: '100%', background: 'black', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p className="text-accent" style={{ animation: 'pulse 1s infinite', fontWeight: 800 }}>● RECORDING...</p>
          </div>
        ) : videoBlob ? (
          <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <video src={URL.createObjectURL(videoBlob)} controls style={{ width: '100%', height: '100%' }} />
            <Button variant="danger" onClick={() => setVideoBlob(null)} style={{ position: 'absolute', top: 10, right: 10, padding: '4px 8px', fontSize: '0.6rem' }}>REDO</Button>
          </div>
        ) : (
          <>
            {currentExercise.videoUrl && (
              <div style={{ width: '100%', marginBottom: 'var(--spacing-md)' }}>
                <VideoEmbed url={currentExercise.videoUrl} title={`${currentExercise.name} demo`} />
              </div>
            )}
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--spacing-md)' }}>{currentExercise.name}</h2>
            <Button variant="outline" onClick={startRecording} style={{ borderRadius: 'var(--radius-full)' }}>
              <span style={{ color: 'var(--status-red)' }}>●</span> RECORD FORM
            </Button>
          </>
        )}
      </Card>

      {isRecording && <Button variant="danger" fullWidth onClick={() => mediaRecorderRef.current?.stop()} style={{ marginBottom: 'var(--spacing-md)' }}>STOP RECORDING</Button>}

      <div style={{ display: 'flex', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-md)' }}>
        <Input label="Weight" type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="0" />
        <Input label="Reps" type="number" value={reps} onChange={(e) => setReps(e.target.value)} placeholder="0" />
      </div>

      <Button fullWidth onClick={handleCompleteSet} isLoading={isSaving} disabled={restTimer > 0 || isRecording}>
        {restTimer > 0 ? `REST (${restTimer}s)` : 'COMPLETE SET'}
      </Button>
    </div>
  )
}

export default WorkoutPlayer
